import * as React from 'react';
import { connect } from 'react-redux';

import BrandsFilter from 'components/brands-filter/BrandsFilter';
import TabsSection from 'components/tabs/tabs-section/TabsSection';
import Pagination from 'components/pagination/Pagination';
import ListDashboardResults from '../ListDashboardResults';

import { getActiveBrandFilterSelector } from '../../../../selectors/brand-filters/brandFilters';
import ClickTutorialHandler from 'modules/help-menu/helpers/ClickTutorialHandler';

import { fetchDashboardLists, resetLists } from '../../reducers/lists';
import { trackEventListsActions } from 'modules/tracking/utils/tracking';
import { brandsFilterUpdated } from 'redux-store/reducers/brands-filter-reducer';

import { __t, applyTranslations } from 'utils/language';
import { getBrandProfileId } from 'utils/organization';
import { setItem } from 'utils/storage/sessionStorage';
import {
  CUSTOM_LIST_TYPE,
  LIST_DASHBOARD_QUERY_PARAMS,
  LIST_DASHBOARD_SORT_OPTIONS,
  LIST_STATE_STORAGE_NAME,
  SMART_LIST_TYPE,
  getQueryValueFromUrl,
  getSortOptions
} from 'utils/list';
import type { ListDashboardTabs } from 'utils/list';
import { updateQueryParams } from 'utils/location';
import { BRAND } from 'utils/brands';
import { VideoKind } from 'modules/help-menu/util/videos';

import './lists-dashboard.scss';

import type { ListScreenState, LocationWindow, Organization, TabsSectionItem } from 'types';

applyTranslations(require('./lists-dashboard.i18n.json'));

const { SEARCH, SORT, PAGE, TAB, FILTER, PER_PAGE } = LIST_DASHBOARD_QUERY_PARAMS;
export type Queries = typeof PAGE | typeof TAB | typeof FILTER | typeof SORT | typeof SEARCH;

type OwnProps = {
  location: LocationWindow
};

type StateProps = {
  screenState: ListScreenState,
  currentOrganization: Organization,
  isLoading: boolean,
  isSearchingOrSorting: boolean,
  activeBrandFilter: string
};

type DispatchProps = {
  dispatchFetchDashboardLists: (
    orgId: number,
    type: string,
    filter: string,
    page: number,
    sort: string,
    search: string,
    perPage: number,
    isSearchingOrSorting: boolean
  ) => Promise<void>,
  dispatchResetLists: () => void,
  dispatchUpdateBrandsFilter: (a: string) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const tabs: Array<TabsSectionItem> = [
  {
    identifier: CUSTOM_LIST_TYPE,
    label: __t('lists_dashboard.tabs.label_custom'),
    tooltip: {
      message: __t('lists_dashboard.tabs.tooltip_custom'),
      width: 'extra-long'
    },
    data: {
      type: CUSTOM_LIST_TYPE
    }
  },
  {
    identifier: SMART_LIST_TYPE,
    label: __t('lists_dashboard.tabs.label_smart'),
    tooltip: {
      message: __t('lists_dashboard.tabs.tooltip_smart'),
      width: 'long'
    },
    data: {
      type: SMART_LIST_TYPE
    }
  }
];

class ListsDashboard extends React.PureComponent<Props> {
  componentDidMount() {
    const activeTab = this.getQueryValue(TAB);
    const brandProfileId = this.getBrandProfileId();

    this.loadData();

    if (brandProfileId) {
      trackEventListsActions(activeTab);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { location } = this.props;
    let sort = getQueryValueFromUrl(location, SORT);
    let search = getQueryValueFromUrl(location, SEARCH);
    let page: string | number = getQueryValueFromUrl(location, PAGE);
    const perPage = getQueryValueFromUrl(location, PER_PAGE);

    const hasBrandFilterChanged = getQueryValueFromUrl(location, FILTER) !== getQueryValueFromUrl(prevProps.location, FILTER);
    const hasTabChanged = getQueryValueFromUrl(location, TAB) !== getQueryValueFromUrl(prevProps.location, TAB);
    const hasSortChanged = sort !== getQueryValueFromUrl(prevProps.location, SORT);
    const hasSearchChanged = search !== getQueryValueFromUrl(prevProps.location, SEARCH);
    const hasPageChanged = page !== getQueryValueFromUrl(prevProps.location, PAGE);
    const hasPerPageChanged = perPage !== getQueryValueFromUrl(prevProps.location, PER_PAGE);

    // Reset sort to default if tab or brand filter has changed
    if (hasBrandFilterChanged || hasTabChanged) {
      sort = getSortOptions(this.getQueryValue(TAB) as ListDashboardTabs, this.getQueryValue(FILTER))[0].value;
      search = '';
    }

    // Reset page number to page 1 if any of brand filter, tab or sort has changed
    if (hasBrandFilterChanged || hasTabChanged || hasSortChanged || hasSearchChanged) {
      page = 1;
    }


    if (hasTabChanged || hasPageChanged || hasBrandFilterChanged || hasSortChanged || hasSearchChanged || hasPerPageChanged) {
      this.setQueryParams({ [SORT]: sort, [PAGE]: page, [SEARCH]: search });
      this.loadData(String(page), sort, search, perPage, hasSortChanged || hasSearchChanged || hasPageChanged);
    }
  }

  componentWillUnmount() {
    const { dispatchResetLists } = this.props;

    dispatchResetLists();
  }

  getQueryValue(type: Queries): string {
    const { location } = this.props;

    return getQueryValueFromUrl(location, type);
  }

  getBrandProfileId(): number | undefined | null {
    const { currentOrganization } = this.props;

    return getBrandProfileId(currentOrganization);
  }

  setQueryParams(query: {}): void {
    const { location } = this.props;
    updateQueryParams(location, query);
  }

  handleTabClick = (identifier?: string | null): void => {
    const brandProfileId = this.getBrandProfileId();

    if (identifier) {
      if (brandProfileId) {
        trackEventListsActions(identifier);
      }

      this.setQueryParams({ tab: identifier });
    }
  };

  handlePageChange = (page: number): void => {
    const { location } = this.props;

    updateQueryParams(location, { [PAGE]: page });
  };

  handleAddList = () => {
    const { dispatchUpdateBrandsFilter } = this.props;
    const mostRecentSort = LIST_DASHBOARD_SORT_OPTIONS.MOST_RECENT.value;
    const firstPage = 1;
    const emptySearch = '';

    this.setQueryParams({
      [PAGE]: firstPage,
      [TAB]: CUSTOM_LIST_TYPE,
      [FILTER]: BRAND,
      [SORT]: mostRecentSort,
      [SEARCH]: emptySearch
    });
    dispatchUpdateBrandsFilter(BRAND);

    this.loadData(String(firstPage), mostRecentSort, emptySearch);
  };

  reloadData = (): void => {
    this.loadData();
  };

  loadData(
    page: string = this.getQueryValue(PAGE),
    sort: string = this.getQueryValue(SORT),
    search: string = this.getQueryValue(SEARCH),
    perPage: string = this.getQueryValue(PER_PAGE),
    isSearchingOrSorting: boolean = false
  ): void {
    const { currentOrganization, location, dispatchFetchDashboardLists, activeBrandFilter } = this.props;
    const orgId = currentOrganization.id;
    const tab = this.getQueryValue(TAB);
    const filter = this.getQueryValue(FILTER) || activeBrandFilter;

    setItem(LIST_STATE_STORAGE_NAME, location.path || '');

    if (orgId) {
      dispatchFetchDashboardLists(
        orgId, tab, filter, Number(page), sort, search, Number(perPage), isSearchingOrSorting
      );
    }
  }

  render(): React.ReactNode {
    const { screenState, location, isLoading, isSearchingOrSorting } = this.props;
    const activeTab = this.getQueryValue(TAB);
    const pagination = screenState.pagination;

    return (
      <div className="lists-dashboard">
        <div className="lists-dashboard__header">
          <div className="lists-dashboard__sort">
            <ClickTutorialHandler event={VideoKind.SHARE_INFORMATION_TEAM}>
              <BrandsFilter />
            </ClickTutorialHandler>
          </div>
        </div>
        <div className="lists-dashboard-results">
          <div className="lists-dashboard-results__tabs">
            <TabsSection
              withShadow
              items={tabs}
              activeTab={activeTab}
              onClick={this.handleTabClick}
            />
          </div>
          <ListDashboardResults
            location={location}
            onDelete={this.reloadData}
            onDuplicate={this.reloadData}
            onAddList={this.handleAddList}
          />
        </div>
        {
          !isLoading && !isSearchingOrSorting && (
            <Pagination
              location={location}
              meta={pagination}
              links={pagination.links}
              onClick={this.handlePageChange}
            />
          )
        }
      </div>
    );
  }
}

const mapStateToProps = (state): StateProps => {
  const { organizations, lists } = state;
  return ({
    activeBrandFilter: getActiveBrandFilterSelector(state),
    currentOrganization: organizations.currentOrganization,
    isLoading: lists.isLoading,
    isSearchingOrSorting: lists.isSearchingOrSorting,
    screenState: lists.screenState
  });
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchDashboardLists: (
    orgId: number,
    type: string,
    filter: string,
    page: number,
    sort: string,
    search: string,
    perPage: number,
    isSearchingOrSorting: boolean = false
  ): Promise<void> => dispatch(
    fetchDashboardLists(orgId, type, filter, page, sort, search, perPage, isSearchingOrSorting)
  ),

  dispatchResetLists: (): void => dispatch(resetLists()),
  dispatchUpdateBrandsFilter: activeBrandFilter => dispatch(brandsFilterUpdated(activeBrandFilter))
});

export default connect(mapStateToProps, mapDispatchToProps)(ListsDashboard);
